import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _toArray from '@babel/runtime/helpers/esm/toArray';
import { isObservable, throwError, concat, of, from, merge, EMPTY } from 'rxjs';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import { del } from 'object-path-immutable';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { map, catchError, filter, switchMap, takeUntil, mergeMap, exhaustMap, expand, tap, groupBy } from 'rxjs/operators';

var INIT = '@@INIT';
var RUN = 'RUN';
var PENDING = 'PENDING';
var SUCCESS = 'SUCCESS';
var FAILURE = 'FAILURE';
var CLEAN = 'CLEAN';
var CANCEL = 'CANCEL';
var UPDATE_DATA = 'UPDATE_DATA';

/**
 * Check if is an effect action
 */
var isEffectAction = function isEffectAction(action) {
  return action['@@RJ/EFFECT'] === true;
};
/**
 * Creates a new library action
 * A library action is a predefined action that can be handled in the context of rocketjump side effect model
 * Such actions are wired into the library and are extremely general: customization with the `actions` directive
 * is provided in order to adapt them (and their interface and behaviour) to user needs
 */

function makeEffectAction(type) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var meta = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var effectAction = {
    type: type,
    payload: {
      params: params
    },
    meta: meta,
    callbacks: {
      onSuccess: undefined,
      onFailure: undefined
    },

    /**
     * This function allows to inject some extra params in a library action
     * It is a delicate operation, since it works by constructing a new action object
     * and rebinding the operations (extends and withMeta) to it
     */
    extend: function extend(extensions) {
      var nextAction = _objectSpread(_objectSpread({}, this), {}, {
        meta: _objectSpread(_objectSpread({}, this.meta), extensions.meta),
        callbacks: {
          onSuccess: extensions.callbacks && extensions.callbacks.onSuccess,
          onFailure: extensions.callbacks && extensions.callbacks.onFailure
        }
      });

      Object.defineProperty(nextAction, '@@RJ/EFFECT', {
        value: true
      });
      return nextAction;
    },

    /**
     * Powerful helper to work with metadata
     * Its arg can either be a plain object, in which case it is merged in , or a function, in which
     *  case meta is hard set to the return value of the function
     */
    withMeta: function withMeta(meta) {
      var nextAction = _objectSpread(_objectSpread({}, this), {}, {
        meta: typeof meta === 'function' ? meta(this.meta) : _objectSpread(_objectSpread({}, this.meta), meta)
      });

      Object.defineProperty(nextAction, '@@RJ/EFFECT', {
        value: true
      });
      return nextAction;
    }
  };
  Object.defineProperty(effectAction, '@@RJ/EFFECT', {
    value: true
  });
  return effectAction;
}

function run() {
  for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
    params[_key] = arguments[_key];
  }

  return makeEffectAction(RUN, params);
}

function clean() {
  for (var _len2 = arguments.length, params = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    params[_key2] = arguments[_key2];
  }

  return makeEffectAction(CLEAN, params);
}

function cancel() {
  for (var _len3 = arguments.length, params = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    params[_key3] = arguments[_key3];
  }

  return makeEffectAction(CANCEL, params);
}

var updateData = function updateData(data) {
  return {
    type: UPDATE_DATA,
    payload: data
  };
};

var ActionCreators = {
  run: run,
  clean: clean,
  cancel: cancel,
  updateData: updateData
};

function defaultMakeSelectors(_ref) {
  var getRoot = _ref.getRoot,
      extraSelectors = _objectWithoutProperties(_ref, ["getRoot"]);

  var getData = function getData(state) {
    return getRoot(state).data;
  };

  var isLoading = function isLoading(state) {
    return getRoot(state).pending;
  };

  var isPending = function isPending(state) {
    return getRoot(state).pending;
  };

  var getError = function getError(state) {
    return getRoot(state).error;
  };

  return _objectSpread(_objectSpread({}, extraSelectors), {}, {
    getRoot: getRoot,
    getData: getData,
    isLoading: isLoading,
    isPending: isPending,
    getError: getError
  });
}

function isPromise(obj) {
  return !!obj && (_typeof(obj) === 'object' || typeof obj === 'function') && typeof obj.then === 'function';
}
function arrayze(a) {
  return Array.isArray(a) ? a : [a];
} // Light get ... only '.' is supported, and obj is expect to be plain shit...

function get(obj, path, defaultValue) {
  var keys = path.split('.');
  var result = obj === null ? undefined : keys.reduce(function (context, current) {
    return context[current];
  }, obj);
  return result === undefined ? defaultValue : result;
}
var getOrSelect = function getOrSelect(obj, selector) {
  if (typeof selector === 'function') {
    return selector(obj);
  }

  return get(obj, selector);
};
function omit(object, props) {
  return props.reduce(function (obj, prop) {
    return del(obj, prop);
  }, object);
}

var RJ_CONFIGURED_EFFECT_CALLER = 'configured';
var RJ_OBJECT = Symbol('^_RJ_OBJ_^');
var RJ_PLUGIN = Symbol('^_RJ_PLUGIN_^');

function isRjConfiguredCaller(a) {
  return a === RJ_CONFIGURED_EFFECT_CALLER;
}
function isObjectRj(a) {
  return (a === null || a === void 0 ? void 0 : a.__rjtype) === RJ_OBJECT;
}
function isPluginRj(a) {
  return (a === null || a === void 0 ? void 0 : a.__rjtype) === RJ_PLUGIN;
}

// Generate a result Observable from a given action
// a RUN action but this is not checked is up to you
// pass the corret action
// in plus emit the PENDING action before invoke the effect
// action => Observable(<PENDING>, <SUCCESS>|<FAILURE>)
function actionMap(action, effectCall, getEffectCaller, prefix) {
  var payload = action.payload,
      meta = action.meta,
      callbacks = action.callbacks;
  var params = payload.params;
  var effectCaller = getEffectCaller(action);
  var effectResult = effectCaller.apply(void 0, [effectCall].concat(_toConsumableArray(params)));

  if (!(isPromise(effectResult) || isObservable(effectResult))) {
    return throwError('The effect result is expect ' + "to be a Promise or an RxObservable but '".concat(effectResult, "' ") + "was given. Please check your effect and effectCaller logic.");
  }

  return concat(of({
    type: prefix + PENDING,
    meta: meta
  }), from(effectResult).pipe(map(function (data) {
    return {
      type: prefix + SUCCESS,
      payload: {
        data: data,
        params: params
      },
      meta: meta,
      // Callback runned from the subscribtion in the react hook
      successCallback: callbacks ? callbacks.onSuccess : undefined
    };
  }), catchError(function (error) {
    // Avoid headache
    if (error instanceof TypeError || error instanceof RangeError || error instanceof SyntaxError || error instanceof ReferenceError) {
      return throwError(error);
    }

    return of({
      type: prefix + FAILURE,
      payload: error,
      meta: meta,
      // Callback runned from the subscribtion in the react hook
      failureCallback: callbacks ? callbacks.onFailure : undefined
    });
  })));
}

var EffectActions = [CLEAN, RUN, CANCEL];

function makeFilterStandarEffectActions(prefix) {
  return function (action) {
    return EffectActions.map(function (a) {
      return prefix + a;
    }).indexOf(action.type) !== -1;
  };
}

function takeUntilCancelAction(actionObservable, prefix) {
  return takeUntil(actionObservable.pipe(filter(function (action) {
    return action.type === prefix + CLEAN || action.type === prefix + CANCEL;
  })));
}

function mapToLatest(actionObservable, effect, getEffectCaller, prefix) {
  return switchMap(function (action) {
    // Switch Map take always the last task so cancel ecc are auto emitted
    if (action.type === prefix + CANCEL || action.type === prefix + CLEAN) {
      return of(action);
    }

    return concat(of(action), actionMap(action, effect, getEffectCaller, prefix)).pipe(takeUntilCancelAction(actionObservable, prefix));
  });
}

function takeEffectLatest(allActionObservable, stateObservable, _ref) {
  var effect = _ref.effect,
      getEffectCaller = _ref.getEffectCaller,
      prefix = _ref.prefix;
  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionObservable.pipe(mapToLatest(actionObservable, effect, getEffectCaller, prefix));
}
function takeEffectEvery(allActionObservable, stateObservable, _ref2) {
  var effect = _ref2.effect,
      getEffectCaller = _ref2.getEffectCaller,
      prefix = _ref2.prefix;
  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionObservable.pipe(mergeMap(function (action) {
    // Marge Map take every
    if (action.type === prefix + CANCEL || action.type === prefix + CLEAN) {
      return of(action);
    }

    return concat(of(action), actionMap(action, effect, getEffectCaller, prefix).pipe(takeUntilCancelAction(actionObservable, prefix)));
  }));
}

function actionToExhaustObservableEffect(actionObservable, effect, getEffectCaller, prefix) {
  return merge(actionObservable.pipe(mergeMap(function (action) {
    if (action.type === prefix + CANCEL || action.type === prefix + CLEAN) {
      return of(action);
    } else {
      return EMPTY;
    }
  })), actionObservable.pipe(exhaustMap(function (action) {
    if (action.type === prefix + CANCEL || action.type === prefix + CLEAN) {
      return EMPTY;
    }

    return concat(of(action), actionMap(action, effect, getEffectCaller, prefix)).pipe(takeUntilCancelAction(actionObservable, prefix));
  })));
}

function takeEffectExhaust(allActionObservable, stateObservable, _ref3) {
  var effect = _ref3.effect,
      getEffectCaller = _ref3.getEffectCaller,
      prefix = _ref3.prefix;
  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionToExhaustObservableEffect(actionObservable, effect, getEffectCaller, prefix);
}

function actionToConcatLatestObservableEffect(actionObservable, effect, getEffectCaller, prefix) {
  var pending = false;
  var queued;
  return actionObservable.pipe(mergeMap(function (action) {
    if (action.type === prefix + CANCEL || action.type === prefix + CLEAN) {
      queued = undefined;
      pending = false;
      return of(action);
    }

    if (pending) {
      queued = action;
      return EMPTY;
    }

    pending = true;
    return concat(of(action), actionMap(action, effect, getEffectCaller, prefix).pipe(expand(function () {
      if (!queued) {
        return EMPTY;
      }

      var nextAction = queued;
      queued = undefined;
      var projected = concat(of(nextAction), actionMap(nextAction, effect, getEffectCaller, prefix));
      return projected;
    }))).pipe(takeUntilCancelAction(actionObservable, prefix), tap({
      complete: function complete() {
        return pending = false;
      }
    }));
  }));
}

function takeEffectConcatLatest(allActionObservable, stateObservable, _ref4) {
  var effect = _ref4.effect,
      getEffectCaller = _ref4.getEffectCaller,
      prefix = _ref4.prefix;
  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionToConcatLatestObservableEffect(actionObservable, effect, getEffectCaller, prefix);
}
function takeEffectGroupBy(allActionObservable, stateObservable, _ref5, groupByFn) {
  var effect = _ref5.effect,
      getEffectCaller = _ref5.getEffectCaller,
      prefix = _ref5.prefix;
  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));

  if (typeof groupByFn !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('[react-rj] when you choose the groupBy ' + 'takeEffect you must provide a function to group by the effect.');
    } else {
      throw new Error('[react-rj] bad config groupBy.');
    }
  }

  return actionObservable.pipe(groupBy(groupByFn), mergeMap(function (groupedObservable) {
    return groupedObservable.pipe(mapToLatest(groupedObservable, effect, getEffectCaller, prefix));
  }));
}
function takeEffectGroupByExhaust(allActionObservable, stateObservable, _ref6, groupByFn) {
  var effect = _ref6.effect,
      getEffectCaller = _ref6.getEffectCaller,
      prefix = _ref6.prefix;

  if (typeof groupByFn !== 'function') {
    throw new Error('[react-rj] when you choose the groupByExhaust ' + 'takeEffect you must provide a function to group by the effect.');
  }

  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionObservable.pipe(groupBy(groupByFn), mergeMap(function (groupedObservable) {
    return actionToExhaustObservableEffect(groupedObservable, effect, getEffectCaller, prefix);
  }));
}
function takeEffectGroupByConcatLatest(allActionObservable, stateObservable, _ref7, groupByFn) {
  var effect = _ref7.effect,
      getEffectCaller = _ref7.getEffectCaller,
      prefix = _ref7.prefix;

  if (typeof groupByFn !== 'function') {
    throw new Error('[react-rj] when you choose the groupByConcatLatest ' + 'takeEffect you must provide a function to group by the effect.');
  }

  var actionObservable = allActionObservable.pipe(filter(makeFilterStandarEffectActions(prefix)));
  return actionObservable.pipe(groupBy(groupByFn), mergeMap(function (groupedObservable) {
    return actionToConcatLatestObservableEffect(groupedObservable, effect, getEffectCaller, prefix);
  }));
}

var _takeEffectsHanlders;
var TAKE_EFFECT_LATEST = 'latest';
var TAKE_EFFECT_EVERY = 'every';
var TAKE_EFFECT_EXHAUST = 'exhaust';
var TAKE_EFFECT_CONCAT_LATEST = 'concatLatest';
var TAKE_EFFECT_GROUP_BY = 'groupBy';
var TAKE_EFFECT_GROUP_BY_EXHAUST = 'groupByExhaust';
var TAKE_EFFECT_GROUP_BY_CONCAT_LATEST = 'groupByConcatLatest';
var takeEffectsHanlders = (_takeEffectsHanlders = {}, _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_LATEST, takeEffectLatest), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_EVERY, takeEffectEvery), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_EXHAUST, takeEffectExhaust), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_CONCAT_LATEST, takeEffectConcatLatest), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_GROUP_BY, takeEffectGroupBy), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_GROUP_BY_EXHAUST, takeEffectGroupByExhaust), _defineProperty(_takeEffectsHanlders, TAKE_EFFECT_GROUP_BY_CONCAT_LATEST, takeEffectGroupByConcatLatest), _takeEffectsHanlders);

// Thanks ma teacher Virgy <3 * GUARDA MAMMA CON I TIPI!!! *
function composeEffectCallers(callers) {
  return function effectCaller(effectCall) {
    function recursion(callers) {
      var _callers = _toArray(callers),
          effectCaller = _callers[0],
          passCallers = _callers.slice(1);

      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      if (passCallers.length === 0) {
        return effectCaller.apply(void 0, [effectCall].concat(args));
      }

      return effectCaller.apply(void 0, [function () {
        for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
          args[_key3] = arguments[_key3];
        }

        return recursion.apply(void 0, [passCallers].concat(args));
      }].concat(args));
    }

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return recursion.apply(void 0, [callers].concat(args));
  };
}

var defaultEffectCaller = function defaultEffectCaller(call) {
  for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
    args[_key4 - 1] = arguments[_key4];
  }

  return call.apply(void 0, args);
};

function makeRunTimeEffectCaller(effectCallers, injectEffectCaller) {
  var realCallers = effectCallers.reduce(function (all, caller) {
    if (isRjConfiguredCaller(caller)) {
      if (injectEffectCaller) {
        all.push(injectEffectCaller);
      }
    } else {
      all.push(caller);
    }

    return all;
  }, []);

  if (realCallers.length === 0) {
    return defaultEffectCaller;
  }

  if (realCallers.length === 1) {
    return realCallers[0];
  }

  return composeEffectCallers(realCallers);
}

function createMakeObservable(_ref) {
  var effectCall = _ref.effect,
      effectCallers = _ref.effectCallers,
      takeEffect = _ref.takeEffect;
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return function makeObservable(actionObservable, stateObservable) {
    // Make run time caller from effect action
    function getEffectCaller(action) {
      var _action$__rjEffectRef, _action$__rjEffectRef2;

      return makeRunTimeEffectCaller(effectCallers, // Defined in rocketjump config
      (_action$__rjEffectRef = action.__rjEffectRef) === null || _action$__rjEffectRef === void 0 ? void 0 : (_action$__rjEffectRef2 = _action$__rjEffectRef.current) === null || _action$__rjEffectRef2 === void 0 ? void 0 : _action$__rjEffectRef2.effectCaller // Run time effect caller
      );
    }

    var handleTakeEffect;

    var _arrayze = arrayze(takeEffect),
        _arrayze2 = _toArray(_arrayze),
        effectType = _arrayze2[0],
        effectTypeArgs = _arrayze2.slice(1);

    if (typeof effectType === 'function') {
      handleTakeEffect = effectType;
    } else {
      // Check valid effectType in DEV
      if (process.env.NODE_ENV !== 'production') {
        if (takeEffectsHanlders[effectType] === undefined) {
          throw new Error("[react-rocketjump] takeEffect: ".concat(takeEffect, " is an invalid effect."));
        }
      }

      handleTakeEffect = takeEffectsHanlders[effectType];
    }

    return handleTakeEffect.apply(void 0, [actionObservable, stateObservable, {
      effect: effectCall,
      getEffectCaller: getEffectCaller,
      prefix: prefix
    }].concat(_toConsumableArray(effectTypeArgs)));
  };
} // GioVa nel posto fa freddo brrrrrrrrrrrrr

function mergeMakeObservables(baseCreator) {
  for (var _len5 = arguments.length, creators = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
    creators[_key5 - 1] = arguments[_key5];
  }

  return function (actionObservable, stateObservable) {
    var baseDispatchObservable = baseCreator(actionObservable, stateObservable);
    var dispatchObservables2Merge = creators.reduce(function (dispatchObservables, rxCreator) {
      var nextDispatchObservable = rxCreator(actionObservable, stateObservable);
      dispatchObservables.push(nextDispatchObservable);
      return dispatchObservables;
    }, [baseDispatchObservable]);
    return merge.apply(void 0, _toConsumableArray(dispatchObservables2Merge));
  };
}

function makeComputeState(computed) {
  if (!computed) {
    return function computeState(state, selectors) {
      return selectors.getRoot(state);
    };
  }

  var computedKeys = Object.keys(computed);
  return function computeState(state, selectors) {
    var nextState = {};

    for (var i = 0; i < computedKeys.length; i++) {
      var key = computedKeys[i];
      var selectorKeyOrFn = computed[key]; // Grab selector from selectors or use selector itself

      var selector = void 0;

      if (typeof selectorKeyOrFn === 'string') {
        selector = selectors[selectorKeyOrFn];

        if (selector === undefined) {
          if (process.env.NODE_ENV !== 'production') {
            throw new Error("[react-rocketjump] you specified a non existing selector [".concat(selectorKeyOrFn, "] ") + "check your computed config.");
          } else {
            throw new Error('[react-rocketjump] @computed error.');
          }
        }
      } else {
        selector = selectorKeyOrFn;
      }

      nextState[key] = selector(state);
    }

    return nextState;
  };
}

var MUTATION_PREFIX = '@MUTATION';
function makeMutationType(name) {
  var subType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return "".concat(MUTATION_PREFIX, "/").concat(name, "/").concat(subType);
}
function matchMutationType(type, matchName, matchSubType) {
  var pieces = type.split('/');

  if (pieces.length !== 3) {
    return null;
  }

  var _pieces = _slicedToArray(pieces, 3),
      name = _pieces[1],
      subType = _pieces[2];

  if (matchName && matchName !== '*') {
    if (Array.isArray(matchName)) {
      if (matchName.indexOf(name) === -1) {
        return null;
      }
    } else if (name !== matchName) {
      return null;
    }
  }

  if (matchSubType && matchSubType !== '*') {
    if (Array.isArray(matchSubType)) {
      if (matchSubType.indexOf(subType) === -1) {
        return null;
      }
    } else if (subType !== matchSubType) {
      return null;
    }
  }

  return [name, subType];
}

var mutationIDCounter = 0; // Make the action creater that trigger a mutation side effects

function makeActionCreator(name, mutation) {
  var isOptimistic = typeof mutation.optimisticResult === 'function'; // Has auto commit when only optimisticUpdater is provided

  var hasAutoCommit = isOptimistic && mutation.optimisticUpdater && !mutation.updater;

  var actionCreator = function actionCreator() {
    for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
      params[_key] = arguments[_key];
    }

    var meta = {
      params: params
    };

    if (isOptimistic) {
      meta.mutationID = ++mutationIDCounter;

      if (hasAutoCommit) {
        meta.mutationAutoCommit = true;
      }
    }

    return makeEffectAction(makeMutationType(name, RUN), params, meta);
  };

  return actionCreator;
} // Add specials rj mutations action creators to base rj action creators


function mutationsActionCreators(mutations, actionCreators) {
  return Object.keys(mutations).reduce(function (enhancedActionCreators, name) {
    var mutation = mutations[name];
    var actionCreator = makeActionCreator(name, mutation);

    if (process.env.NODE_ENV !== 'production' && actionCreators[name]) {
      console.warn("[react-rocketjump] @mutations WARNING the mutation [".concat(name, "] ") + "override a pre existing action creator this can leading to " + "unexpected behaviors.");
    }

    return _objectSpread(_objectSpread({}, enhancedActionCreators), {}, _defineProperty({}, name, actionCreator));
  }, actionCreators);
}

function getMutations(state) {
  return state.mutations;
}

var mutationsSelectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getMutations: getMutations
});

// Stolen from https://github.com/reduxjs/redux
function combineReducers(reducers) {
  var reducerKeys = Object.keys(reducers);
  return function combination() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var action = arguments.length > 1 ? arguments[1] : undefined;
    var hasChanged = false;
    var nextState = {};

    for (var i = 0; i < reducerKeys.length; i++) {
      var key = reducerKeys[i];
      var reducer = reducers[key];
      var previousStateForKey = state[key];
      var nextStateForKey = reducer(previousStateForKey, action);
      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }

    hasChanged = hasChanged || reducerKeys.length !== Object.keys(state).length;
    return hasChanged ? nextState : state;
  };
}

// enhance the basic reducer \w updater of mutations to rj root reducer
function mutationsHorRoot(mutations, reducer, actionCreators) {
  var handleMutationsReducers = Object.keys(mutations).reduce(function (handlers, name) {
    var mutation = mutations[name];
    var update, optimisticUpdate;

    if (typeof mutation.optimisticUpdater === 'function' || typeof mutation.optimisticUpdater === 'string') {
      // Got optimisticUpdater
      // Check optimisticResult to be define when got optimisticUpdater
      if (typeof mutation.optimisticResult !== 'function') {
        if (process.env.NODE_ENV === 'production') {
          throw new Error('[react-rocketjump] @mutations error.');
        } else {
          throw new Error('[react-rocketjump] @mutations you should define optimisticUpdater ' + "along with optimisticResult check your mutation config [".concat(name, "]."));
        }
      } // Check for good optimisticUpdater action creator


      if (typeof mutation.optimisticUpdater === 'string') {
        var actionCreator = actionCreators[mutation.optimisticUpdater];

        if (typeof actionCreator !== 'function') {
          if (process.env.NODE_ENV === 'production') {
            throw new Error('[react-rocketjump] @mutations error.');
          } else {
            throw new Error("[react-rocketjump] @mutations you provide a non existing " + "action creator [".concat(mutation.updater, "] as optimisticUpdater for mutation [").concat(name, "]."));
          }
        } // Use action creator as optmistic update handler


        optimisticUpdate = function optimisticUpdate(state, action) {
          return reducer(state, actionCreator(action.payload.data));
        };
      } else {
        optimisticUpdate = function optimisticUpdate(state, action) {
          return (// NOTE: https://github.com/microsoft/TypeScript/issues/9998
            mutation.optimisticUpdater(state, action.payload.data)
          );
        };
      }
    } // No optimisticUpdater
    // Check for good updater


    if (typeof mutation.updater === 'string') {
      var _actionCreator = actionCreators[mutation.updater];

      if (typeof _actionCreator !== 'function') {
        if (process.env.NODE_ENV === 'production') {
          throw new Error('[react-rocketjump] @mutations error.');
        } else {
          throw new Error("[react-rocketjump] @mutations you provide a non existing " + "action creator [".concat(mutation.updater, "] as updater for mutation [").concat(name, "]."));
        }
      }

      update = function update(state, action) {
        return reducer(state, _actionCreator(action.payload.data));
      };
    } else if (typeof mutation.updater === 'function') {
      update = function update(state, action) {
        return (// NOTE: https://github.com/microsoft/TypeScript/issues/9998
          mutation.updater(state, action.payload.data)
        );
      };
    } else if (!optimisticUpdate) {
      // Get angry only when we have no a valid optimisticUpdate
      if (process.env.NODE_ENV === 'production') {
        throw new Error('[react-rocketjump] @mutations error.');
      } else {
        throw new Error('[react-rocketjump] @mutations you should provide at least ' + "an effect and an updater to mutation config [".concat(name, "]."));
      }
    } // Register updater as SUCCESS handler


    if (update) {
      var type = makeMutationType(name, SUCCESS);
      handlers[type] = update;
    } // Optmistic updater!


    if (typeof mutation.optimisticResult === 'function') {
      // Use standard updater ans optimistiUpdater when is not defined
      if (!optimisticUpdate) {
        optimisticUpdate = update;
      }

      var _type = makeMutationType(name, RUN);

      handlers[_type] = function (state, action) {
        // NOTE: https://github.com/microsoft/TypeScript/issues/9998
        var optimisticData = mutation.optimisticResult.apply(mutation, _toConsumableArray(action.payload.params));
        return optimisticUpdate(state, {
          payload: {
            data: optimisticData
          }
        });
      };
    }

    return handlers;
  }, {});
  return function (prevState, action) {
    if (handleMutationsReducers[action.type]) {
      return handleMutationsReducers[action.type](prevState, action);
    }

    return reducer(prevState, action);
  };
} // Reducer for track the mutation state

function makeMutationReducer(mutationReducer, name) {
  return function (state, action) {
    if (action.type === INIT) {
      return mutationReducer(state, action);
    }

    var matchesMutationType = matchMutationType(action.type, name);

    if (matchesMutationType) {
      var _matchesMutationType = _slicedToArray(matchesMutationType, 2),
          decoupleType = _matchesMutationType[1];

      return mutationReducer(state, _objectSpread(_objectSpread({}, action), {}, {
        type: decoupleType
      }));
    }

    return state;
  };
} // Mutations reducer or null if no mutations has a reducer config option


function makeMutationsReducer(mutations) {
  var mutationsReducers = Object.keys(mutations).reduce(function (all, name) {
    var mutation = mutations[name];

    if (typeof mutation.reducer !== 'function') {
      return all;
    }

    return _objectSpread(_objectSpread({}, all), {}, _defineProperty({}, name, makeMutationReducer(mutation.reducer, name)));
  }, {});

  if (Object.keys(mutationsReducers).length === 0) {
    return null;
  }

  return combineReducers(mutationsReducers);
}
var DefaultOptState = {
  snapshot: null,
  actions: []
};
function optimisticMutationsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DefaultOptState;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (state.snapshot) {
    return _objectSpread(_objectSpread({}, state), {}, {
      actions: state.actions.concat({
        committed: true,
        action: action
      })
    });
  }

  return state;
}

function handleOptRun(reducer, state, action) {
  var _state$optimisticMuta = state.optimisticMutations,
      actions = _state$optimisticMuta.actions,
      snapshot = _state$optimisticMuta.snapshot;
  var nextActions = actions.concat({
    committed: false,
    action: action
  });
  var nextSnapshot = snapshot !== null && snapshot !== void 0 ? snapshot : state.root;
  var nextState = reducer(state, action);
  return _objectSpread(_objectSpread({}, nextState), {}, {
    optimisticMutations: {
      snapshot: nextSnapshot,
      actions: nextActions
    }
  });
}

function applyActionsOnSnapshot(snapshot, actions, reducer) {
  var state = {
    root: snapshot
  };
  return actions.reduce(function (snap, action) {
    return reducer(snap, action);
  }, state).root;
}

function getFirstNonCommittedIndex(actions) {
  for (var i = 0; i < actions.length; i++) {
    if (!actions[i].committed) {
      return i;
    }
  }

  return null;
}

function handleOptSuccess(reducer, state, action) {
  var _state$optimisticMuta2 = state.optimisticMutations,
      actions = _state$optimisticMuta2.actions,
      snapshot = _state$optimisticMuta2.snapshot; // Commit action
  // SWAP THE RUN \W SUCCESS KEEP ORDER BUT USE SERVER RESPONSE
  // OR WHEN AUTO COMMIT ACTIVE COMMIT THE GOOD RUN!

  var mutationTypeRun = action.type.split('/').slice(0, 2).concat(RUN).join('/');
  var nextActions = actions.map(function (actionWrapper) {
    var _actionWrapper$action, _actionWrapper$action2;

    if (actionWrapper.action.type === mutationTypeRun && ((_actionWrapper$action = actionWrapper.action) === null || _actionWrapper$action === void 0 ? void 0 : (_actionWrapper$action2 = _actionWrapper$action.meta) === null || _actionWrapper$action2 === void 0 ? void 0 : _actionWrapper$action2.mutationID) === action.meta.mutationID) {
      return {
        committed: true,
        action: action.meta.mutationAutoCommit === true ? actionWrapper.action : action
      };
    } else {
      return actionWrapper;
    }
  });
  var commitedRootState;

  if (action.meta.mutationAutoCommit === true) {
    // Use current root state and commit them!
    commitedRootState = state.root;
  } else {
    // Commited root state \w SUCCESS from SERVER
    commitedRootState = applyActionsOnSnapshot(snapshot, nextActions.map(function (a) {
      return a.action;
    }), reducer);
  }

  var firstNonCommitIndex = getFirstNonCommittedIndex(nextActions);
  var nextSnapshot;

  if (firstNonCommitIndex === null) {
    // All commited!
    nextSnapshot = null;
    nextActions = [];
  } else {
    // Save a new snapshot appling actions until first non committed
    nextSnapshot = applyActionsOnSnapshot(snapshot, nextActions.slice(0, firstNonCommitIndex).map(function (a) {
      return a.action;
    }), reducer); // Take only action from first non committed

    nextActions = nextActions.slice(firstNonCommitIndex);
  }

  var nextState = reducer(state, action);
  return _objectSpread(_objectSpread({}, nextState), {}, {
    root: commitedRootState,
    optimisticMutations: {
      snapshot: nextSnapshot,
      actions: nextActions
    }
  });
}

function handleOptFailure(reducer, state, action) {
  var _state$optimisticMuta3 = state.optimisticMutations,
      actions = _state$optimisticMuta3.actions,
      snapshot = _state$optimisticMuta3.snapshot; // Remove failied RUN

  var mutationTypeRun = action.type.split('/').slice(0, 2).concat(RUN).join('/');
  var nextActions = actions.filter(function (a) {
    var _a$action, _a$action$meta;

    return a.action.type !== mutationTypeRun || ((_a$action = a.action) === null || _a$action === void 0 ? void 0 : (_a$action$meta = _a$action.meta) === null || _a$action$meta === void 0 ? void 0 : _a$action$meta.mutationID) !== action.meta.mutationID;
  }); // 0 - 1 - 1 - 0 - 1
  // FILTER:
  // 1 - 1 - 0 - 1
  // or
  // 0 - 1 - 1 - 1
  // Rollback to state without opt failed actions

  var roolBackRootState = applyActionsOnSnapshot(snapshot, nextActions.map(function (a) {
    return a.action;
  }), reducer);
  var firstNonCommitIndex = getFirstNonCommittedIndex(nextActions);
  var nextSnapshot;

  if (firstNonCommitIndex === null) {
    // All committed!
    nextSnapshot = null;
    nextActions = [];
  } else {
    // 0 - 1 - 1 - 1
    // Squash the action that will be removed into a new snap
    nextSnapshot = applyActionsOnSnapshot(snapshot, nextActions.slice(0, firstNonCommitIndex).map(function (a) {
      return a.action;
    }), reducer); // 0 - 1
    // or
    // 0 - 1 - 1 - 1
    // from

    nextActions = nextActions.slice(firstNonCommitIndex);
  }

  var nextState = reducer(state, action);
  return _objectSpread(_objectSpread({}, nextState), {}, {
    root: roolBackRootState,
    optimisticMutations: {
      snapshot: nextSnapshot,
      actions: nextActions
    }
  });
}

function optimisticMutationsHor(reducer) {
  return function (state, action) {
    var _meta;

    if (Number.isInteger(action === null || action === void 0 ? void 0 : (_meta = action.meta) === null || _meta === void 0 ? void 0 : _meta.mutationID)) {
      // OPT ACTIONS
      var matchesMutationType = matchMutationType(action.type);

      if (matchesMutationType) {
        var _matchesMutationType2 = _slicedToArray(matchesMutationType, 2),
            decoupleType = _matchesMutationType2[1];

        if (decoupleType === RUN) {
          return handleOptRun(reducer, state, action);
        }

        if (decoupleType === FAILURE) {
          return handleOptFailure(reducer, state, action);
        }

        if (decoupleType === SUCCESS) {
          return handleOptSuccess(reducer, state, action);
        }
      }
    }

    return reducer(state, action);
  };
}

function makeExtraRxObservables(mutations, _ref) {
  var effectCallers = _ref.effectCallers;
  var makeMutationsObsList = Object.keys(mutations).map(function (name) {
    var _mutations$name = mutations[name],
        effect = _mutations$name.effect,
        takeEffect = _mutations$name.takeEffect,
        effectCaller = _mutations$name.effectCaller;
    var prefix = makeMutationType(name);

    if (typeof effect !== 'function') {
      throw new Error('[react-rocketjump] @mutations you should provide at least ' + "an effect and an updater to mutation config [".concat(name, "]."));
    }

    var mutationEffectCallers = [];

    if (typeof effectCaller === 'function') {
      // If defined used the mutation effect caller (ONLY)
      mutationEffectCallers = [effectCaller];
    } else if (effectCaller === false) {
      // Explict false no effect caller (empty list)
      mutationEffectCallers = [];
    } else {
      mutationEffectCallers = effectCallers;
    }

    return createMakeObservable({
      effect: effect,
      takeEffect: takeEffect || 'every',
      effectCallers: mutationEffectCallers
    }, prefix);
  });
  return makeMutationsObsList;
}

function createMutationsRjObjectEnhancer(mutations) {
  var reducersToCombine = {};
  var mutationsReducer = makeMutationsReducer(mutations);

  if (mutationsReducer) {
    reducersToCombine.mutations = mutationsReducer;
  }

  var hasSomeOptimisticMutations = Object.keys(mutations).some(function (name) {
    return typeof mutations[name].optimisticResult === 'function';
  });

  if (hasSomeOptimisticMutations) {
    // Enable optimistic reducer...
    reducersToCombine.optimisticMutations = optimisticMutationsReducer;
  }

  var enhanceReducer;

  if (hasSomeOptimisticMutations) {
    enhanceReducer = optimisticMutationsHor;
  }

  return {
    actionCreators: function actionCreators(_actionCreators) {
      return mutationsActionCreators(mutations, _actionCreators);
    },
    enhanceRootReducer: function enhanceRootReducer(reducer, actionCreators) {
      return mutationsHorRoot(mutations, reducer, actionCreators);
    },
    enhanceReducer: enhanceReducer,
    reducersToCombine: reducersToCombine,
    selectors: mutationsSelectors,
    makeObservables: function makeObservables(sideEffect) {
      return makeExtraRxObservables(mutations, sideEffect);
    }
  };
}

function composeReducers() {
  for (var _len = arguments.length, reducers = new Array(_len), _key = 0; _key < _len; _key++) {
    reducers[_key] = arguments[_key];
  }

  return function (prevState, action) {
    return reducers.reduce(function (nextState, reducer) {
      return reducer(nextState, action);
    }, prevState);
  };
}

// Barebone reducer for handle an async effect
var defaultState = {
  pending: false,
  error: null,
  data: null
};
function rootReducer() {
  var prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;
  var action = arguments.length > 1 ? arguments[1] : undefined;
  var type = action.type;

  switch (type) {
    case PENDING:
      return _objectSpread(_objectSpread({}, prevState), {}, {
        error: null,
        pending: true
      });

    case FAILURE:
      return _objectSpread(_objectSpread({}, prevState), {}, {
        pending: false,
        error: action.payload
      });

    case SUCCESS:
      return _objectSpread(_objectSpread({}, prevState), {}, {
        pending: false,
        data: action.payload.data
      });

    case CANCEL:
      return _objectSpread(_objectSpread({}, prevState), {}, {
        pending: false
      });

    case CLEAN:
      // So easy if someone add some shit to state
      // simply preserve that keys!
      return _objectSpread(_objectSpread({}, prevState), defaultState);

    case UPDATE_DATA:
      return _objectSpread(_objectSpread({}, prevState), {}, {
        data: action.payload
      });

    default:
      return prevState;
  }
}

function createRjObject() {
  return {
    reducer: rootReducer,
    combineReducers: {},
    makeSelectors: defaultMakeSelectors,
    actionCreators: ActionCreators,
    takeEffect: TAKE_EFFECT_LATEST,
    effectCallers: [],
    effectPipelines: [],
    addSideEffects: []
  };
}
function mergeRjObject(config, mergeObj) {
  var _config$takeEffect;

  // Start with reducer
  var reducer;

  if (config.reducer) {
    reducer = config.reducer(mergeObj.reducer);
  } else {
    reducer = mergeObj.reducer;
  } // Kompose reducer


  if (config.composeReducer) {
    reducer = composeReducers(reducer, config.composeReducer);
  }

  var combineReducers = _objectSpread(_objectSpread({}, mergeObj.combineReducers), config.combineReducers);

  var makeSelectors;

  if (config.selectors) {
    // Kompose next make selectors
    makeSelectors = function makeSelectors(selectors) {
      var nextSelectors = mergeObj.makeSelectors(selectors);
      var configSelectors = config.selectors(nextSelectors);
      return _objectSpread(_objectSpread({}, nextSelectors), configSelectors);
    };
  } else {
    makeSelectors = mergeObj.makeSelectors;
  }

  var actionCreators = config.actions ? _objectSpread(_objectSpread({}, mergeObj.actionCreators), config.actions(mergeObj.actionCreators)) : mergeObj.actionCreators; // Override take from config when given

  var takeEffect = (_config$takeEffect = config.takeEffect) !== null && _config$takeEffect !== void 0 ? _config$takeEffect : mergeObj.takeEffect;
  var effectCallers = config.effectCaller ? // Merge next caller
  mergeObj.effectCallers.concat(config.effectCaller) : // Keep prev caller
  mergeObj.effectCallers;
  var effectPipelines = config.effectPipeline ? // Merge next caller
  mergeObj.effectPipelines.concat(config.effectPipeline) : // Keep prev caller
  mergeObj.effectPipelines;
  var addSideEffects = config.addSideEffect ? // Merge next side effects
  mergeObj.addSideEffects.concat(config.addSideEffect) : // Keep prev side effects
  mergeObj.addSideEffects;
  return {
    reducer: reducer,
    makeSelectors: makeSelectors,
    combineReducers: combineReducers,
    actionCreators: actionCreators,
    takeEffect: takeEffect,
    effectCallers: effectCallers,
    effectPipelines: effectPipelines,
    addSideEffects: addSideEffects
  };
}
function finalizeRjObject(config, mergeObj) {
  var _withMutations$enhanc, _withMutations$enhanc2, _withMutations$enhanc3, _withMutations$enhanc4, _withMutations$action, _withMutations$makeOb;

  // Hello,
  // we are at the of recursion
  // This are taken from the rj() objet creator or rj.effect() with builder
  var effect = config.effect,
      mutations = config.mutations,
      name = config.name; // These are all the option merged along the rj plugin chain
  // rj(rjPlugin()) merged according to mergeRjObject

  var configRootReducer = mergeObj.reducer,
      combineReducersMap = mergeObj.combineReducers,
      baseMakeSelectors = mergeObj.makeSelectors,
      configActionCreators = mergeObj.actionCreators,
      takeEffect = mergeObj.takeEffect,
      effectCallers = mergeObj.effectCallers,
      effectPipelines = mergeObj.effectPipelines,
      addSideEffects = mergeObj.addSideEffects; // Mutations
  // Init the mutations enhancher with mutations config
  // this add features to final rj object export reducer, action ecc

  var withMutations = mutations ? createMutationsRjObjectEnhancer(mutations) : null; // REDUCER
  // Rj Root Reducer

  var rjRootReducer = (_withMutations$enhanc = withMutations === null || withMutations === void 0 ? void 0 : (_withMutations$enhanc2 = withMutations.enhanceRootReducer) === null || _withMutations$enhanc2 === void 0 ? void 0 : _withMutations$enhanc2.call(withMutations, configRootReducer, configActionCreators)) !== null && _withMutations$enhanc !== void 0 ? _withMutations$enhanc : configRootReducer; // Rj Reducers with others map and mutations map + other future vendors maps

  if (process.env.NODE_ENV !== 'production') {
    var _withMutations$reduce;

    if (combineReducersMap.root) {
      console.warn("[react-rocketjump] You try to override the [root] key that is a reserverd key " + 'please use reducer or composeReducer config option to override root reducer.');
    }

    Object.keys((_withMutations$reduce = withMutations === null || withMutations === void 0 ? void 0 : withMutations.reducersToCombine) !== null && _withMutations$reduce !== void 0 ? _withMutations$reduce : {}).forEach(function (mutationKey) {
      if (combineReducersMap[mutationKey]) {
        console.warn("[react-rocketjump] You try to override a mutation reserved key [".concat(mutationKey, "] ") + 'this has no effect.');
      }
    });
  }

  var rjReducer = combineReducers(_objectSpread(_objectSpread(_objectSpread({}, combineReducersMap), withMutations === null || withMutations === void 0 ? void 0 : withMutations.reducersToCombine), {}, {
    root: rjRootReducer
  })); // ++Mutations Future other enchanchers EHehehe

  var reducer = (_withMutations$enhanc3 = withMutations === null || withMutations === void 0 ? void 0 : (_withMutations$enhanc4 = withMutations.enhanceReducer) === null || _withMutations$enhanc4 === void 0 ? void 0 : _withMutations$enhanc4.call(withMutations, rjReducer)) !== null && _withMutations$enhanc3 !== void 0 ? _withMutations$enhanc3 : rjReducer; // Selectors

  var getRoot = function getRoot(state) {
    return state.root;
  };

  var makeSelectors = function makeSelectors() {
    return (// Init the make selectors chain with the root + extra reducers
      // for now mutations ...future hook into with other selectors
      baseMakeSelectors(_objectSpread({
        getRoot: getRoot
      }, mutationsSelectors))
    );
  }; // Action Creators


  var actionCreators = (_withMutations$action = withMutations === null || withMutations === void 0 ? void 0 : withMutations.actionCreators(configActionCreators)) !== null && _withMutations$action !== void 0 ? _withMutations$action : configActionCreators; // Computed

  var computeState = makeComputeState(config.computed); // Side Effect

  var createEffectConfig = {
    effect: effect,
    takeEffect: takeEffect,
    effectCallers: effectCallers
  };
  var makeObservable = createMakeObservable(createEffectConfig);
  var extraMakeObservables = ((_withMutations$makeOb = withMutations === null || withMutations === void 0 ? void 0 : withMutations.makeObservables(createEffectConfig)) !== null && _withMutations$makeOb !== void 0 ? _withMutations$makeOb : []).concat(addSideEffects.map(function (addTakeEffect) {
    return createMakeObservable(_objectSpread(_objectSpread({}, createEffectConfig), {}, {
      takeEffect: addTakeEffect
    }));
  }));

  if (extraMakeObservables.length) {
    makeObservable = mergeMakeObservables.apply(void 0, [makeObservable].concat(_toConsumableArray(extraMakeObservables)));
  }

  var pipeActionStream = function pipeActionStream(actions, state) {
    return effectPipelines.reduce(function (actions, piper) {
      return piper(actions, state);
    }, actions);
  };

  var rjObject = {
    // Optional Rj Name =)
    name: name,
    reducer: reducer,
    makeSelectors: makeSelectors,
    actionCreators: actionCreators,
    computeState: computeState,
    makeObservable: makeObservable,
    pipeActionStream: pipeActionStream
  };
  Object.defineProperty(rjObject, '__rjtype', {
    value: RJ_OBJECT
  });
  return rjObject;
}

function craftRjPlugin(config, plugins) {
  var plugIn = function plugIn(givenObj) {
    var wihtCurriedPluginObj = plugins.reduce(function (mergeObj, plugin) {
      return plugin(mergeObj);
    }, givenObj);
    return mergeRjObject(config, wihtCurriedPluginObj);
  };

  Object.defineProperty(plugIn, '__rjtype', {
    value: RJ_PLUGIN
  });
  return plugIn;
}

function rjPluginEndBuilder(config, plugins) {
  return {
    // Finally craft plugin
    build: function build() {
      return craftRjPlugin(config, plugins);
    }
  };
}

function rjPluginEffectConfigBuilder(config, plugins) {
  return _objectSpread(_objectSpread({}, rjPluginEndBuilder(config, plugins)), {}, {
    // ++ Side Effect
    effect: function effect(sideEffect) {
      return rjPluginEndBuilder(_objectSpread(_objectSpread({}, config), sideEffect), plugins);
    }
  });
}

function rjPluginSelecotrsConfigBuilder(config, plugins) {
  return _objectSpread(_objectSpread({}, rjPluginEffectConfigBuilder(config, plugins)), {}, {
    // ++ Selectors
    selectors: function (_selectors) {
      function selectors(_x) {
        return _selectors.apply(this, arguments);
      }

      selectors.toString = function () {
        return _selectors.toString();
      };

      return selectors;
    }(function (selectors) {
      return rjPluginEffectConfigBuilder(_objectSpread(_objectSpread({}, config), {}, {
        selectors: selectors
      }), plugins);
    })
  });
}

function rjPluginReducerAtionsConfigBuilder(config, plugins) {
  return _objectSpread(_objectSpread({}, rjPluginSelecotrsConfigBuilder(config, plugins)), {}, {
    // ++ Actions
    actions: function (_actions) {
      function actions(_x2) {
        return _actions.apply(this, arguments);
      }

      actions.toString = function () {
        return _actions.toString();
      };

      return actions;
    }(function (actions) {
      return rjPluginReducerAtionsConfigBuilder(_objectSpread(_objectSpread({}, config), {}, {
        actions: actions
      }), plugins);
    }),
    // ++ Reducer
    reducer: function (_reducer) {
      function reducer(_x3) {
        return _reducer.apply(this, arguments);
      }

      reducer.toString = function () {
        return _reducer.toString();
      };

      return reducer;
    }(function (reducer) {
      return rjPluginReducerAtionsConfigBuilder(_objectSpread(_objectSpread({}, config), {}, {
        reducer: reducer
      }), plugins);
    }),
    // ++ Compose Reducer
    composeReducer: function (_composeReducer) {
      function composeReducer(_x4) {
        return _composeReducer.apply(this, arguments);
      }

      composeReducer.toString = function () {
        return _composeReducer.toString();
      };

      return composeReducer;
    }(function (composeReducer) {
      return rjPluginReducerAtionsConfigBuilder(_objectSpread(_objectSpread({}, config), {}, {
        composeReducer: composeReducer
      }), plugins);
    }),
    // ++ Combine Reducers
    combineReducers: function (_combineReducers) {
      function combineReducers(_x5) {
        return _combineReducers.apply(this, arguments);
      }

      combineReducers.toString = function () {
        return _combineReducers.toString();
      };

      return combineReducers;
    }(function (combineReducers) {
      return rjPluginReducerAtionsConfigBuilder(_objectSpread(_objectSpread({}, config), {}, {
        combineReducers: combineReducers
      }), plugins);
    })
  });
}

function rjPluginBuilder() {
  return _objectSpread(_objectSpread({}, rjPluginReducerAtionsConfigBuilder({}, [])), {}, {
    plugins: function (_plugins) {
      function plugins() {
        return _plugins.apply(this, arguments);
      }

      plugins.toString = function () {
        return _plugins.toString();
      };

      return plugins;
    }(function () {
      for (var _len = arguments.length, plugins = new Array(_len), _key = 0; _key < _len; _key++) {
        plugins[_key] = arguments[_key];
      }

      return rjPluginReducerAtionsConfigBuilder({}, plugins);
    })
  });
}

function rjPlugin() {
  for (var _len = arguments.length, configAndPlugins = new Array(_len), _key = 0; _key < _len; _key++) {
    configAndPlugins[_key] = arguments[_key];
  }

  if (configAndPlugins.length === 0) {
    return rjPluginBuilder();
  }

  var _ref = configAndPlugins.slice(-1),
      _ref2 = _slicedToArray(_ref, 1),
      config = _ref2[0];

  var plugins = configAndPlugins.slice(0, -1);
  return craftRjPlugin(config, plugins);
}

export { CLEAN as C, FAILURE as F, INIT as I, PENDING as P, RJ_CONFIGURED_EFFECT_CALLER as R, SUCCESS as S, UPDATE_DATA as U, isObjectRj as a, makeEffectAction as b, createRjObject as c, actionMap as d, takeEffectEvery as e, finalizeRjObject as f, get as g, takeEffectExhaust as h, isEffectAction as i, takeEffectConcatLatest as j, takeEffectGroupBy as k, takeEffectGroupByExhaust as l, mergeRjObject as m, takeEffectGroupByConcatLatest as n, RUN as o, CANCEL as p, makeMutationType as q, rjPlugin as r, matchMutationType as s, takeEffectLatest as t, isRjConfiguredCaller as u, isPluginRj as v, getOrSelect as w, omit as x };
